import { ICompassusNavigatorRepository } from "../../adapters/driven/repositories/compassusNavigatorRepository";

export const CompassusNavigatorServiceImplFactory = ({
  compassusNavigatorRepository,
}: {
  compassusNavigatorRepository: ICompassusNavigatorRepository;
}) => {
  const getPatients: IGetCompassusPatients = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getPatientsAPI({
        clinic_id: clinicId,
        params: queryParams,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientIds: IGetCompassusPatientIds = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getPatientIdsAPI({
        clinic_id: clinicId,
        params: queryParams,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const exportNavigator: IExportCompassusNavigator = async ({
    clinicId,
    body,
  }) => {
    try {
      const response = await compassusNavigatorRepository.exportNavigatorAPI({
        clinic_id: clinicId,
        body,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getCounts: IGetCompassusCounts = async ({ clinicId, queryParams }) => {
    try {
      const response = await compassusNavigatorRepository.getCountsAPI({
        clinic_id: clinicId,
        params: queryParams,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPayerFilters: IGetCompassusPayerFilters = async ({ clinicId }) => {
    try {
      const response = await compassusNavigatorRepository.getPayerFiltersAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getMedicationFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getMedicationFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getAllergyFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getAllergyFiltersAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getConditionFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getConditionFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getLastVisitProviderFilters: IGetCompassusProviderFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getLastVisitProviderFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getNextVisitProviderFilters: IGetCompassusProviderFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getNextVisitProviderFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getTodoTagFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getTodoTagFiltersAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getCustomTagFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getCustomTagFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getClinicalRiskScoreFilters: IGetCompassusClinicalRiskScoreFilters =
    async ({ clinicId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getClinicalRiskScoreFiltersAPI({
            clinic_id: clinicId,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const patchPatientTodo: IPatchCompassusPatientTodo = async ({
    clinicId,
    patientId,
    todoTags,
  }) => {
    try {
      const response = await compassusNavigatorRepository.patchPatientTodoAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        body: { tags: todoTags },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const patchPatientCustomTags: IPatchCompassusPatientCustomTags = async ({
    clinicId,
    patientId,
    tags,
    hospitalizationRecords,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.postPatientCustomTagsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
          body: { tags, hospitalization_records: hospitalizationRecords },
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientCustomTags: IGetCompassusPatientCustomTags = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientCustomTagsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientAssociations: IGetCompassusPatientAssociations = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientAssociationsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  /**
   * Bulk to-do mutation — one HTTP request for any number of patients. See
   * `IBulkPatchCompassusPatientTodo` in the driven port for mode semantics.
   */
  const bulkPatchPatientTodo: IBulkPatchCompassusPatientTodo = async ({
    clinicId,
    patientIds,
    mode,
    todoTags,
  }) => {
    try {
      const body: ICompassusBulkPatchTodoRequest = {
        patient_ids: patientIds,
        mode,
      };
      // Backend ignores todo_tags when mode="clear", but omitting avoids
      // sending a superfluous field and matches the spec's "can be omitted".
      if (mode !== "clear") body.todo_tags = todoTags ?? [];
      const response =
        await compassusNavigatorRepository.bulkPatchPatientTodoAPI({
          clinic_id: clinicId,
          body,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const putWatchlist: IPutCompassusWatchlist = async ({ clinicId, items }) => {
    try {
      const response = await compassusNavigatorRepository.putWatchlistAPI({
        clinic_id: clinicId,
        body: { items },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getAcuityLevelFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getAcuityLevelFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientAcuityLevelHistory: IGetCompassusPatientAcuityLevelHistory =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientAcuityLevelHistoryAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientPrognosisHistory: IGetCompassusPatientPrognosisHistory =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientPrognosisHistoryAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientFormHistory: IGetCompassusPatientFormHistory = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientFormHistoryAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getFormsSchema: IGetCompassusFormsSchema = async ({ clinicId }) => {
    try {
      const response = await compassusNavigatorRepository.getFormsSchemaAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  return {
    getPatients,
    getPatientIds,
    exportNavigator,
    getCounts,
    getPayerFilters,
    getMedicationFilters,
    getAllergyFilters,
    getConditionFilters,
    getLastVisitProviderFilters,
    getNextVisitProviderFilters,
    getTodoTagFilters,
    getCustomTagFilters,
    getClinicalRiskScoreFilters,
    patchPatientTodo,
    patchPatientCustomTags,
    getPatientCustomTags,
    getPatientAssociations,
    bulkPatchPatientTodo,
    putWatchlist,
    getAcuityLevelFilters,
    getPatientAcuityLevelHistory,
    getPatientPrognosisHistory,
    getPatientFormHistory,
    getFormsSchema,
  };
};
